// --- external
import { createMachine, assign, spawn, sendTo, pure } from "xstate";

// --- internal
import DACmachine from "./dac.machine";

import services from "./services";
import { useQuery } from "../query";
import { basketSubscription } from "../basketProduct/helper";
import { authSubscription } from "../session/helper";

// --- utils
import {
  ErrorOrigin,
  mapToHeadlessError,
  responseCodes,
  type ResponseError,
  useTime,
  DOMAIN_LIKE_VALIDATION
} from "../../utils";
import {
  getDomainRawBasketProducts,
  isBasketTransfer,
  isDomainProduct,
  parseDomain
} from "./utils";
import {
  cloneDeep,
  compact,
  defaultsDeep,
  find,
  first,
  get,
  has,
  includes,
  isArray,
  isEmpty,
  isString,
  last,
  map,
  reduce,
  remove,
  set,
  some,
  trim,
  values
} from "lodash-es";

// --- types
import type { AnyEventObject } from "xstate";
import { type IBasketProduct } from "@upmind-automation/types";
import { DomainTypes, DomainMode } from "./types";
import type { DomainModel, DomainContext, DomainProduct } from "./types";
import { parseBasketProduct } from "../basketProduct/utils";
import { type ProductProps } from "../product";
import { useI18n } from "../system";
import { useFeedback } from "../feedback";

// -----------------------------------------------------------------------------
export default createMachine(
  {
    id: "domainManager",
    predictableActionArguments: true,
    initial: "subscribing",
    context: {} as DomainContext,
    states: {
      subscribing: {
        entry: ["setContext", "clearLookups"],
        always: {
          target: "loading",
          actions: ["setBasketHelper", "setAuthHelper"]
        }
      },

      loading: {
        id: "loading",
        type: "parallel",
        entry: [],
        states: {
          existing: {
            initial: "processing",
            states: {
              processing: {
                invoke: {
                  src: "getClientDomains",
                  onDone: {
                    target: "complete",
                    actions: ["setOwned"]
                  },
                  onError: { target: "complete" }
                }
              },
              complete: { type: "final" }
            }
          },
          basket: {
            initial: "processing",
            states: {
              processing: {
                entry: ["loadBasket"],
                on: {
                  REFRESH: {
                    target: "complete",
                    actions: ["refreshContext", "setBasketProducts"]
                  },
                  ERROR: {
                    target: "complete"
                  }
                }
              },
              complete: { type: "final" }
            }
          }
        },
        onDone: "idle",
        exit: ["checkModel", "ensureSelected", "persistModel"]
      },

      // our initial state depends on if the machine has been forced to a type,
      // if we do then go to that types state, otherwise stay idle
      idle: {
        entry: ["checkChoices", "checkType"],
        id: "idle",
        always: [
          {
            target: "skip",
            cond: "isSkip"
          },
          {
            target: "dac",
            cond: ({ type }) => {
              return includes(
                [DomainTypes.register, DomainMode.transfer],
                type
              );
            }
          },
          {
            target: "existing",
            cond: ({ type }) => type === DomainTypes.existing
          },
          {
            target: "basket",
            cond: ({ type }) => type === DomainTypes.basket
          }
        ]
      },

      skip: {
        entry: ["setSkipModel"]
      },

      dac: {
        invoke: {
          id: "dac",
          src: DACmachine,
          data: ({
            type,
            search,
            currency,
            basketId,
            brandId,
            coupons,
            preferredCycle,
            tlds,
            useSuggestions
          }: DomainContext) => ({
            mode: type,
            search,
            currency,
            basketId,
            brandId,
            coupons,
            preferredCycle,
            tlds,
            useSuggestions
          }),
          autoForward: true,
          onError: {
            target: "idle",
            actions: ["setError"]
          },
          onDone: [
            {
              target: "#basket",
              cond: "hasDacDomains",
              actions: [
                "setModelFromDac",
                "ensureSelected",
                "checkChoices",
                "setTypeBasket",
                "persistModel"
              ]
            },
            {
              target: "#idle", // NB go back to start and let it work out where to go
              actions: ["setModelFromDac", "ensureSelected", "checkType"]
            }
          ]
        },
        on: {
          STOP: { actions: sendTo("dac", { type: "STOP" }) }
        },
        exit: ["clearSearch"]
      },

      existing: {
        id: "existing",
        entry: ["setTransferFromBasket"],
        initial: "invalid",
        states: {
          invalid: {
            always: [
              {
                target: "transferred",
                cond: "hasTransferProductId"
              },
              {
                target: "validating",
                cond: "hasModelDomainLike"
              }
            ]
          },

          validating: {
            entry: ["cancelAvailability", "setCheckingDomain"],
            always: [
              {
                target: "transferred",
                cond: "isTransferInBasket",
                actions: ["setTransferFromBasket"]
              },
              {
                target: "valid",
                cond: "isOwnedDomain",
                actions: ["persistModel"]
              }
            ],
            invoke: {
              src: "checkAvailability",
              onDone: [
                {
                  target: "checked",
                  cond: "isDomainTransferable",
                  actions: ["setAvailabilityResult"]
                },
                {
                  target: "registerable",
                  cond: "isDomainRegisterable",
                  actions: ["setAvailabilityResult"]
                },
                {
                  target: "unavailable",
                  cond: "isDomainUnavailable"
                },
                {
                  target: "valid",
                  actions: ["persistModel"]
                }
              ],
              onError: {
                target: "error",
                actions: ["setError"]
              }
            }
          },

          checked: {
            on: {
              ADD_TRANSFER: { target: "transferring" }
            }
          },

          registerable: {
            on: {
              ADD_REGISTRATION: { target: "registering" }
            }
          },

          registering: {
            invoke: {
              src: "addExistingRegistration",
              onDone: {
                target: "valid",
                actions: [
                  "setModelFromRegistration",
                  "ensureSelected",
                  "checkChoices",
                  "persistModel"
                ]
              },
              onError: {
                target: "registerable",
                actions: ["setError", "setFeedbackRegistrationAddError"]
              }
            }
          },

          transferring: {
            invoke: {
              src: "addExistingTransfer",
              onDone: {
                target: "transferred",
                actions: ["setTransferProductId"]
              },
              onError: {
                target: "checked",
                actions: ["setError", "setFeedbackTransferAddError"]
              }
            }
          },

          transferred: {
            on: {
              REMOVE_TRANSFER: { target: "removing" }
            }
          },

          removing: {
            on: {
              UPDATE: { actions: ["storePendingUpdate"] },
              CHOOSE: { actions: ["storePendingChoose"] }
            },
            invoke: {
              src: "removeExistingTransfer",
              onDone: [
                {
                  target: "#idle",
                  cond: "hasPendingChoose",
                  actions: [
                    "applyPendingChoose",
                    "clearPendingChoose",
                    "clearPendingUpdate",
                    "clearModel",
                    "clearCheckingDomain",
                    "clearAvailabilityResult",
                    "clearTransferProductId"
                  ]
                },
                {
                  target: "validating",
                  cond: "hasPendingUpdateDomainLike",
                  actions: [
                    "setExistingFromPending",
                    "persistModel",
                    "clearPendingUpdate",
                    "clearAvailabilityResult",
                    "clearTransferProductId"
                  ]
                },
                {
                  target: "invalid",
                  cond: "hasPendingUpdate",
                  actions: [
                    "setExistingFromPending",
                    "persistModel",
                    "clearPendingUpdate",
                    "clearAvailabilityResult",
                    "clearTransferProductId"
                  ]
                },
                {
                  target: "checked",
                  actions: ["clearTransferProductId"]
                }
              ],
              onError: [
                {
                  target: "transferred",
                  cond: "hasPendingChoose",
                  actions: [
                    "setError",
                    "setFeedbackTransferRemoveError",
                    "clearPendingChoose",
                    "clearPendingUpdate"
                  ]
                },
                {
                  target: "transferred",
                  actions: [
                    "setError",
                    "setFeedbackTransferRemoveError",
                    "clearPendingUpdate"
                  ]
                }
              ]
            }
          },

          valid: {},
          unavailable: {},
          error: {}
        },
        on: {
          REFRESH: {
            actions: ["setBasketProducts", "refreshContext", "checkChoices"]
          },
          // NB: The `removing` child state overrides UPDATE to queue the value
          // via `storePendingUpdate` instead of transitioning. XState v4 resolves
          // child handlers before parent, so this handler only fires when NOT in
          // the `removing` state.
          UPDATE: [
            {
              target: ".validating",
              cond: "isDomainLike",
              // XState v4 quirk: relative targets (`.foo`) default to
              // `internal: true`, which means a self-transition from
              // `validating → .validating` wouldn't exit and re-enter
              // — entry actions (`cancelAvailability`, `setCheckingDomain`)
              // and `invoke` wouldn't fire on the new keystroke, leaving
              // the domain stuck on the previous value. Force external so
              // every UPDATE genuinely re-enters validating and the new
              // `/availability` call gets issued.
              internal: false,
              actions: ["clearError", "setExisting", "persistModel"]
            },
            {
              target: ".invalid",
              internal: false,
              actions: ["setErrorInvalidDomain", "setExisting", "persistModel"]
            }
          ]
        },
        exit: [
          "clearModel",
          "clearCheckingDomain",
          "clearAvailabilityResult",
          "clearTransferProductId"
        ]
      },

      basket: {
        id: "basket",
        entry: ["resetModel", "checkModel", "ensureSelected"],
        initial: "loading",
        states: {
          loading: {
            after: {
              wait: "invalid"
            }
          },
          processing: {
            after: {
              wait: "invalid"
            }
          },
          valid: {
            always: {
              target: "invalid",
              cond: "isInvalid"
            }
          },
          invalid: {
            always: {
              target: "valid",
              cond: "isValid"
            }
          },

          error: {},
          complete: {}
        },
        on: {
          REFRESH: {
            target: "#idle",
            cond: "isBasketEmptyFromEvent",
            actions: ["setBasketProducts", "clearModel", "clearType"]
          },
          SELECT: [
            {
              target: ".processing",
              actions: ["select", "persistModel"],
              cond: "isSelectable"
            }
          ]
        },
        exit: ["clearModel"]
      },

      complete: {
        type: "final"
      }
    },
    on: {
      REFRESH: {
        actions: [
          "setBasketProducts",
          "refreshContext",
          "checkChoices",
          "checkType"
        ]
      },

      CHOOSE: [
        {
          // Already removing — absorb the new target type, don't restart removal
          cond: "isInRemovingState",
          actions: ["storePendingChoose"]
        },
        {
          // do nothing
          cond: "isInvalidType"
        },
        {
          target: "skip",
          actions: ["setType"],
          cond: "isSkipType"
        },
        {
          target: "dac",
          actions: ["setType"],
          cond: "isDomainRegister"
        },
        {
          target: "dac",
          actions: ["setType"],
          cond: "isDomainTransfer"
        },
        {
          target: "existing",
          actions: ["setType"],
          cond: "isExistingDomain"
        },
        {
          target: "basket",
          actions: ["setType", "ensureBasketModel", "persistModel"],
          cond: "isBasket"
        }
      ],

      STOP: {
        target: "complete"
      },

      AUTHENTICATED: {
        target: "loading",
        actions: ["clearLookups"]
      },
      UNAUTHENTICATED: {
        target: "loading",
        actions: ["clearLookups"]
      }
    }
  },
  {
    actions: {
      setContext: assign((context: DomainContext, _event: AnyEventObject) => {
        return defaultsDeep(context, {
          choices: values(DomainTypes),
          type: undefined,
          required: true,
          model: undefined,
          lookups: {
            owned: [],
            basket: []
          },
          // ---
          currency: undefined,
          basketId: undefined,
          brandId: undefined,
          coupons: [],
          // ---
          error: undefined,
          // --- existing flow
          checkingDomain: undefined,
          availability: undefined,
          transferProductId: undefined,
          pendingUpdate: undefined,
          pendingChoose: undefined,

          // ---
          authHelper: undefined,
          basketHelper: undefined,
          parseBasketProduct: undefined,
          parseProductModel: undefined
        }) as DomainContext;
      }),

      persistModel: assign({
        baseModel: ({ model }: DomainContext) => cloneDeep(model) // we use spread to ensure its a new array
      }),

      checkModel: assign({
        model: ({ model }: DomainContext) => {
          const value = parseDomain(model);

          // ensure the selected flag is set on the selected domain
          if (value) set(value, "selected", true);

          return value;
        }
      }),

      ensureSelected: assign({
        model: ({ model }: DomainContext) => {
          if (!isEmpty(model) && !model.selected) {
            set(model, "selected", true);
          }
          return model;
        }
      }),

      ensureBasketModel: assign({
        model: ({ model, baseModel, lookups }: DomainContext) => {
          // The source state's exit actions run BEFORE this transition action
          // (XState v4 order: exit → transition → entry), so `existing.exit`'s
          // `clearModel` will have wiped `model` when changing from existing.
          // Fall back to `baseModel` — the last persisted user choice — so
          // we don't lose the user's domain and silently jump to the first
          // basket item.
          const effectiveModel = model ?? baseModel;
          const domain = get(effectiveModel, "domain");
          if (domain && some(lookups.basket, ["domain", domain])) {
            return effectiveModel;
          }
          const fallback = first(lookups.basket);
          if (fallback) {
            return {
              domain: fallback.domain,
              tld: fallback.tld,
              sld: fallback.sld,
              type: DomainTypes.basket,
              selected: true
            } as DomainModel;
          }
          return undefined;
        }
      }),

      checkChoices: assign({
        choices: ({ lookups, choices, required }: DomainContext) => {
          choices ??= [];
          if (isString(choices)) choices = [choices as DomainTypes];

          if (required) {
            remove(choices, value => value === DomainTypes.skip);
          }
          // add skip when explicitly optional and not already present
          else if (!includes(choices, DomainTypes.skip)) {
            choices.unshift(DomainTypes.skip);
          }

          // ensure we DONT have the basket type in the choices if we dont have any basket products
          if (isEmpty(lookups.basket)) {
            remove(choices, value => value === DomainTypes.basket);
          }
          // nb only add the basket choice if we are not restricting choices
          else if (choices.length > 1 && !includes(choices, DomainTypes.basket))
            choices.push(DomainTypes.basket);

          return choices;
        }
      }),

      checkType: assign({
        type: ({ type, choices, model, lookups, search }: DomainContext) => {
          // FORCE: if we dont have a type set but we have an initial search query
          if (
            !type &&
            includes(choices, DomainTypes.register) &&
            !isEmpty(search?.query)
          ) {
            return DomainTypes.register;
          }

          const domain = get(model, "domain");

          // FORCE: if we have a domain AND we are not limiting the choices
          if (
            domain &&
            (includes(choices, DomainTypes.basket) ||
              includes(choices, DomainTypes.existing))
          ) {
            const matched = find(lookups.basket, ["domain", domain]);
            if (matched) {
              return DomainTypes.basket;
            }
            return DomainTypes.existing;
          }

          if (!type && !isEmpty(choices)) {
            if (includes(choices, DomainTypes.register)) {
              return DomainTypes.register;
            }
            return first(choices);
          }

          return type;
        }
      }),

      setType: assign({
        type: (_context, { data }: AnyEventObject) => data,
        error: undefined
      }),

      refreshContext: assign(
        (_context: DomainContext, { data }: AnyEventObject) => {
          if (isEmpty(data)) return {};
          const { id: basketId, brand_id: brandId, currency } = data;

          const newContext = {
            basketId,
            brandId,
            currency: currency?.code
          };

          return newContext;
        }
      ),

      setAuthHelper: assign(({ authHelper }: DomainContext) => ({
        authHelper: authHelper || spawn(authSubscription)
      })),

      loadBasket: pure(({ basketHelper }: DomainContext, _event) => {
        if (!basketHelper) return;
        return sendTo(basketHelper, {
          type: "INIT"
        });
      }),

      setBasketHelper: assign(({ basketHelper }: DomainContext) => {
        // only do this once, set up the basket helper
        return {
          basketHelper: basketHelper ?? spawn(basketSubscription),

          parseBasketProduct: (
            raw: IBasketProduct,
            primaryDomain?: string
          ): DomainProduct | undefined => {
            if (
              !isDomainProduct({
                serviceIdentifier: raw.service_identifier,
                blueprintCode:
                  raw?.product?.provision_blueprint?.category?.code,
                provisionFields: raw?.provision_fields
              })
            )
              return undefined;

            const value = raw?.service_identifier;
            const parsed = value ? parseDomain(value) : undefined;

            if (!parsed) return undefined;

            const basketProduct = parseBasketProduct(raw) as DomainProduct;
            basketProduct.tld = parsed.tld;
            basketProduct.sld = parsed.sld;
            basketProduct.domain = parsed.domain;
            basketProduct.meta.isTransfer = isBasketTransfer(raw);
            basketProduct.meta.selected = parsed.domain === primaryDomain;
            basketProduct.productDetails.title = parsed.domain;

            return basketProduct;
          },

          parseProductModel: (
            item: DomainProduct
          ): ProductProps | undefined => {
            if (!item?.configuration?.productId) return undefined;
            return item.configuration;
          }
        };
      }),

      setBasketProducts: assign({
        lookups: (
          { lookups, parseBasketProduct, model }: DomainContext,
          { data }: AnyEventObject
        ) => {
          const primary = model || first(lookups.basket);
          // 1st filter out only the domain products from the basket products
          const domains = getDomainRawBasketProducts(
            data?.products as IBasketProduct[]
          );
          // then parse them into our DomainProduct type
          const available = reduce(
            domains,
            (result: DomainProduct[], raw: IBasketProduct) => {
              const parsed = parseBasketProduct(raw, primary?.domain);

              // NB ensure we have a valid domain and we dont already have it in the list
              if (parsed && !some(result, ["domain", parsed.domain]))
                result.push(parsed);

              return result;
            },
            []
          );

          set(lookups, "basket", available);
          return lookups;
        }
      }),

      // ---

      setExisting: assign({
        model: (_context: DomainContext, { data }: AnyEventObject) => {
          const value = trim(isArray(data) ? first(data) : data);
          const parsed = parseDomain(value);
          const domain: DomainModel = {
            type: DomainTypes.existing,
            domain: parsed?.domain ?? value,
            tld: parsed?.tld ?? "",
            sld: parsed?.sld ?? "",
            selected: true
          };
          return domain;
        }
      }),

      setExistingFromPending: assign({
        model: (context: DomainContext) => {
          const value = trim(context.pendingUpdate ?? "");
          const parsed = parseDomain(value);
          return {
            type: DomainTypes.existing,
            domain: value,
            tld: parsed?.tld ?? "",
            sld: parsed?.sld ?? "",
            selected: true
          } as DomainModel;
        }
      }),

      setModelFromDac: assign({
        model: (
          { model, lookups }: DomainContext,
          { data }: AnyEventObject
        ) => {
          // if no data, return existing model
          if (isEmpty(data?.domains)) return model;
          const mapped = map(data.domains, (item: DomainProduct) =>
            parseDomain(item.domain)
          );
          // Most-recent wins: the user expects the domain they just added to
          // be the one linked to the product. DAC's `complete.data` marks
          // `last(model)` as `selected`; `parseDomain` drops the marker, so
          // fall back to `last` here to keep both sides aligned.
          return find(mapped, "selected") || last(mapped);
        },
        lookups: ({ lookups }: DomainContext, { data }: AnyEventObject) => {
          if (isEmpty(data?.basket)) return lookups;
          lookups.basket = data.basket;
          return lookups;
        }
      }),

      clearModel: assign({
        model: () => undefined
      }),

      resetModel: assign({
        model: ({ baseModel }, _event: AnyEventObject) => cloneDeep(baseModel)
      }),

      setOwned: assign({
        lookups: ({ lookups }: DomainContext, { data }: AnyEventObject) => {
          const available = map(compact(data), (item: DomainModel) => {
            return {
              domain: item.domain,
              tld: item.tld,
              sld: item.sld,
              productDetails: {
                title: item.domain
              },
              meta: {
                owned: true,
                persisted: true
              }
            } as DomainProduct;
          });
          set(lookups, "owned", available);
          return lookups;
        }
      }),

      clearLookups: assign({
        lookups: (_context: DomainContext, _event: AnyEventObject) => {
          return {
            owned: [],
            basket: []
          };
        }
      }),

      select: assign({
        model: ({ lookups }: DomainContext, { data }: AnyEventObject) => {
          const selected =
            find(lookups.basket, ["domain", data]) || first(lookups.basket);

          if (selected) {
            return {
              domain: selected.domain,
              tld: selected.tld,
              sld: selected.sld,
              type: DomainTypes.basket,
              selected: true
            } as DomainModel;
          }
          return undefined;
        }
      }),

      clearSearch: assign({
        search: () => {
          return undefined;
        }
      }),

      setError: assign({
        error: (_context, { data }: AnyEventObject) => {
          return mapToHeadlessError(data);
        }
      }),

      setErrorInvalidDomain: assign({
        error: (_context: DomainContext, { data }: AnyEventObject) => {
          const { t } = useI18n();
          return {
            data: null,
            status: responseCodes.Unprocessable_Entity,
            message: t("error.domain_not_valid"),
            origin: ErrorOrigin.Headless
          } as ResponseError;
        }
      }),

      clearError: assign({ error: undefined }),

      setSkipModel: assign({
        model: () => null
      }),

      clearType: assign({
        type: () => undefined
      }),

      setTypeBasket: assign({
        type: () => DomainTypes.basket
      }),

      storePendingUpdate: assign({
        pendingUpdate: (_context: DomainContext, { data }: AnyEventObject) =>
          trim(isArray(data) ? first(data) : data)
      }),

      clearPendingUpdate: assign({
        pendingUpdate: () => undefined
      }),

      storePendingChoose: assign({
        pendingChoose: (_context: DomainContext, { data }: AnyEventObject) =>
          data
      }),

      clearPendingChoose: assign({
        pendingChoose: () => undefined
      }),

      applyPendingChoose: assign({
        type: (context: DomainContext) => {
          const target = context.pendingChoose;
          if (target && includes(context.choices, target)) return target;
          console.warn(
            "[domain] pendingChoose target no longer in choices, falling back",
            { target, choices: context.choices }
          );
          return undefined;
        },
        error: undefined
      }),

      setCheckingDomain: assign({
        checkingDomain: (ctx: DomainContext) => ctx.model?.domain
      }),

      clearCheckingDomain: assign({
        checkingDomain: () => undefined
      }),

      // Aborts any in-flight `/availability` fetch from a prior keystroke.
      // The user types -> UPDATE -> re-enters `validating` -> a new invoke
      // fires. Without this, the previous fetch keeps running (XState v4
      // can't cancel a Promise actor; it just ignores its result), wasting
      // bandwidth and risking out-of-order responses. `cancelQueries`
      // signals TanStack to abort the active queryFn — propagation to
      // `fetch` relies on `checkAvailability`'s queryFn forwarding the
      // signal via `init.signal` (see `services.ts`).
      cancelAvailability: () => {
        useQuery().cancel(["domains", "availability"]);
      },

      setAvailabilityResult: assign({
        availability: (_context: DomainContext, { data }: AnyEventObject) =>
          data
      }),

      clearAvailabilityResult: assign({
        availability: () => undefined
      }),

      setTransferProductId: assign({
        transferProductId: (
          _context: DomainContext,
          { data }: AnyEventObject
        ) => data?.bpid
      }),

      setTransferFromBasket: assign({
        transferProductId: ({
          lookups,
          model,
          transferProductId
        }: DomainContext) => {
          const domain = get(model, "domain");
          if (!domain) return transferProductId;
          // `lookups.basket` holds registration products too. Filter on
          // `meta.isTransfer === true` so a registered domain doesn't get
          // misrouted as a transfer (existing → transferred flip + a
          // REMOVE_TRANSFER that hits the wrong basket product id).
          const matched = find(
            lookups.basket,
            (item: DomainProduct) =>
              item.domain === domain && item.meta?.isTransfer === true
          );
          return matched?.id ?? transferProductId;
        }
      }),

      clearTransferProductId: assign({
        transferProductId: () => undefined
      }),

      setFeedbackTransferAddError: (context: DomainContext) => {
        const { t } = useI18n();
        useFeedback().addError({
          title: t("domain.error.transfer_add_failed"),
          copy: context.checkingDomain
        });
      },

      setFeedbackTransferRemoveError: (context: DomainContext) => {
        const { t } = useI18n();
        useFeedback().addError({
          title: t("domain.error.transfer_remove_failed"),
          copy: context.checkingDomain
        });
      },

      setModelFromRegistration: assign({
        model: (_ctx: DomainContext, { data }: AnyEventObject) => {
          if (!data?.domain) return undefined;
          return parseDomain(data.domain) as DomainModel;
        }
      }),

      setFeedbackRegistrationAddError: (context: DomainContext) => {
        const { t } = useI18n();
        useFeedback().addError({
          title: t("domain.error.registration_add_failed"),
          copy: context.checkingDomain
        });
      }
    },

    guards: {
      isSkip: ({ type }: DomainContext) => type === DomainTypes.skip,

      isSkipType: ({ choices }: DomainContext, { data }: AnyEventObject) =>
        !isEmpty(choices) && data === DomainTypes.skip,

      isBasketEmptyFromEvent: (
        _context: DomainContext,
        { data }: AnyEventObject
      ) => {
        const products = data?.products;
        return isArray(products) && products.length === 0;
      },

      hasDacDomains: (_context: DomainContext, { data }: AnyEventObject) =>
        !isEmpty(data?.domains),

      // NB: This guard inspects the current machine state via the third
      // argument provided by XState v4 guards. Raw `state.matches()` is used
      // because `stateMatches` is a composable-level utility, not available
      // inside machine configuration.
      isInRemovingState: (
        _ctx: DomainContext,
        _event: AnyEventObject,
        { state }: { state: { matches: (value: string) => boolean } }
      ) => state.matches("existing.removing"),

      isOwnedDomain: ({ model, lookups }: DomainContext) =>
        some(lookups.owned, ["domain", model?.domain]),

      isDomainTransferable: (
        _context: DomainContext,
        { data }: AnyEventObject
      ) => !data?.can_register && data?.can_transfer === true,

      isDomainRegisterable: (
        _context: DomainContext,
        { data }: AnyEventObject
      ) => data?.can_register === true,

      isDomainUnavailable: (
        _context: DomainContext,
        { data }: AnyEventObject
      ) => data?.can_register === false && data?.can_transfer === false,

      hasPendingChoose: ({ pendingChoose }: DomainContext) =>
        pendingChoose !== undefined,

      hasTransferProductId: ({ transferProductId }: DomainContext) =>
        !!transferProductId,

      isTransferInBasket: ({ model, lookups }: DomainContext) =>
        some(lookups.basket, {
          domain: get(model, "domain"),
          meta: { isTransfer: true }
        }),

      hasModelDomainLike: ({ model }: DomainContext) => {
        const domain = get(model, "domain");
        return !!domain && DOMAIN_LIKE_VALIDATION.test(domain);
      },

      hasPendingUpdateDomainLike: ({ pendingUpdate }: DomainContext) =>
        !!pendingUpdate && DOMAIN_LIKE_VALIDATION.test(pendingUpdate),

      hasPendingUpdate: ({ pendingUpdate }: DomainContext) =>
        pendingUpdate !== undefined,

      isInvalidType: (
        { choices, type }: DomainContext,
        { data }: AnyEventObject
      ) => {
        return isEmpty(choices) || !has(DomainTypes, data) || type == data;
      },

      isValid: ({ model }: DomainContext) => !isEmpty(parseDomain(model)),

      isSelectable: (
        { model, lookups }: DomainContext,
        { data }: AnyEventObject
      ) => {
        const valid = some(lookups.basket, ["domain", data]);
        return valid;
      },

      isInvalid: ({ model }: DomainContext) => isEmpty(parseDomain(model)),

      isDomainLike: (_context: DomainContext, { data }: AnyEventObject) => {
        return !!data && DOMAIN_LIKE_VALIDATION.test(String(data));
      },

      isDomainTransfer: (
        { choices }: DomainContext,
        { data }: AnyEventObject
      ) => !isEmpty(choices) && data === DomainMode.transfer,

      isExistingDomain: (
        { choices }: DomainContext,
        { data }: AnyEventObject
      ) => !isEmpty(choices) && data === DomainTypes.existing,

      isDomainRegister: (
        { choices }: DomainContext,
        { data }: AnyEventObject
      ) => !isEmpty(choices) && data === DomainTypes.register,

      isBasket: ({ choices }: DomainContext, { data }: AnyEventObject) =>
        !isEmpty(choices) && data === DomainTypes.basket
    },

    delays: {
      error: () => useTime().ERROR,
      wait: () => useTime().WAIT
    },

    services: {
      getClientDomains: services.getClientDomains,
      checkAvailability: services.checkAvailability,
      addExistingTransfer: services.addExistingTransfer,
      addExistingRegistration: services.addExistingRegistration,
      removeExistingTransfer: services.removeExistingTransfer
    }
  }
);
